import { Plus, Trash2 } from "lucide-react";

import { Button } from "../ui/button.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card.js";
import { Input } from "../ui/input.js";
import { Label } from "../ui/label.js";
import { SelectField } from "../ui/select-field.js";
import { ReferenceStatusBadge } from "./ReferenceStatusBadge.js";

/** A former employer, or a university for someone with no work history yet. */
export type ReferenceType = "PROFESSIONAL" | "ACADEMIC";

export interface Reference {
  /** Set once saved; absent on a new row. */
  id?: string;
  name: string; relationship: string; referenceType: ReferenceType; company: string; designation: string; phone: string; email: string;
  /** HR's review (read-only here). Anything but PENDING locks the row. */
  status?: string;
  hrComments?: string;
}

export const MIN_REFERENCES = 2;
export const EMPTY_REFERENCE: Reference = { name: "", relationship: "", referenceType: "PROFESSIONAL", company: "", designation: "", phone: "", email: "" };

// The two fields whose wording depends on the kind of reference. `company`
// holds the employer or the institution, so only the labels differ.
const TYPE_COPY: Record<ReferenceType, { company: string; designation: string; relationship: string }> = {
  PROFESSIONAL: { company: "Company *",               designation: "Their designation",          relationship: "e.g. Former manager" },
  ACADEMIC:     { company: "University / college *",  designation: "Their department or subject", relationship: "e.g. Final-year project guide" },
};

const INACTIVE = new Set(["UNREACHABLE", "ALTERNATE_REQUESTED"]);
export const isEditableReference = (r: Reference) => !r.status || r.status === "PENDING";
const isBlank = (r: Reference) => !r.name.trim() && !r.company.trim() && !r.phone.trim() && !r.relationship.trim();

// Pads the list with blank rows so the employee always sees enough slots to
// reach the minimum (flagged references no longer count).
export function withReferenceSlots(refs: Reference[]): Reference[] {
  const active = refs.filter(r => !INACTIVE.has(r.status ?? "")).length;
  // Rows saved before reference types existed arrive without one; default them
  // so the type dropdown stays a controlled input.
  const typed = refs.map(r => (r.referenceType ? r : { ...r, referenceType: "PROFESSIONAL" as const }));
  return [...typed, ...Array.from({ length: Math.max(0, MIN_REFERENCES - active) }, () => ({ ...EMPTY_REFERENCE }))];
}

/** Rows to send: only ones the employee may still edit, without HR's read-only fields. */
export function referencesPayload(refs: Reference[]) {
  return refs.filter(r => isEditableReference(r) && !isBlank(r))
    .map(({ status: _s, hrComments: _c, ...r }) => r);
}

export function validateReferences(refs: Reference[]): string | null {
  const editable = refs.filter(r => isEditableReference(r) && !isBlank(r));
  if (editable.some(r => !r.name.trim() || !r.relationship.trim() || !r.company.trim() || !r.phone.trim())) {
    return "Fill in the name, how you know them, phone, and the company or university for each reference.";
  }
  const usable = editable.length + refs.filter(r => r.status === "VERIFIED").length;
  return usable < MIN_REFERENCES ? `Add at least ${MIN_REFERENCES} professional references.` : null;
}

// Wizard step 6: professional references HR will contact. Rows HR has already
// reviewed are shown read-only with HR's comment; flagged ones ask for an alternate.
export function ReferencesEditor({ references, onChange }: { references: Reference[]; onChange: (next: Reference[]) => void }) {
  const update = (i: number, k: keyof Reference, v: string) =>
    onChange(references.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const editableCount = references.filter(isEditableReference).length;

  return (
    <Card className="mb-5">
      <CardHeader>
        <CardTitle>References</CardTitle>
        <CardDescription>
          At least {MIN_REFERENCES} people who can vouch for your work — a former manager or senior colleague, or,
          if you haven't worked before, a university professor or project guide. HR will contact them.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {references.map((r, i) => {
          const id = `ref-${i}`;
          if (!isEditableReference(r)) {
            const flagged = r.status === "UNREACHABLE" || r.status === "ALTERNATE_REQUESTED";
            return (
              <div key={r.id ?? id} className="rounded-md border border-surface-200 bg-surface-50 px-3.5 py-2.5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[13px] font-semibold">{r.name}</span>
                  <span className="text-xs text-surface-500">{r.relationship} · {[r.designation, r.company].filter(Boolean).join(", ")} · {r.phone}</span>
                  <ReferenceStatusBadge status={r.status} />
                </div>
                {r.hrComments && (
                  <p className={flagged ? "mt-1.5 text-xs text-[var(--color-warning-fg)]" : "mt-1.5 text-xs text-surface-600"}>
                    HR: {r.hrComments}{flagged && " — please add another reference below."}
                  </p>
                )}
              </div>
            );
          }
          const copy = TYPE_COPY[r.referenceType] ?? TYPE_COPY.PROFESSIONAL;
          return (
            <div key={r.id ?? id} className="rounded-md border border-surface-200 p-3.5">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-[13px] font-semibold">
                  Reference {i + 1}
                  {r.id && <ReferenceStatusBadge status={r.status} />}
                </span>
                {editableCount > MIN_REFERENCES && (
                  <Button variant="ghost" size="sm" onClick={() => onChange(references.filter((_, idx) => idx !== i))}>
                    <Trash2 size={12} /> Remove
                  </Button>
                )}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Label htmlFor={`${id}-type`}>Reference type *</Label>
                  <SelectField id={`${id}-type`} value={r.referenceType} onChange={e => update(i, "referenceType", e.target.value)}>
                    <option value="PROFESSIONAL">Previous employer</option>
                    <option value="ACADEMIC">University or college</option>
                  </SelectField>
                </div>
                <div><Label htmlFor={`${id}-name`}>Full name *</Label><Input id={`${id}-name`} value={r.name} onChange={e => update(i, "name", e.target.value)} /></div>
                <div><Label htmlFor={`${id}-rel`}>How you know them *</Label><Input id={`${id}-rel`} value={r.relationship} onChange={e => update(i, "relationship", e.target.value)} placeholder={copy.relationship} /></div>
                <div><Label htmlFor={`${id}-company`}>{copy.company}</Label><Input id={`${id}-company`} value={r.company} onChange={e => update(i, "company", e.target.value)} /></div>
                <div><Label htmlFor={`${id}-desig`}>{copy.designation}</Label><Input id={`${id}-desig`} value={r.designation} onChange={e => update(i, "designation", e.target.value)} placeholder="Optional" /></div>
                <div><Label htmlFor={`${id}-phone`}>Phone *</Label><Input id={`${id}-phone`} type="tel" value={r.phone} onChange={e => update(i, "phone", e.target.value)} /></div>
                <div><Label htmlFor={`${id}-email`}>Email</Label><Input id={`${id}-email`} type="email" value={r.email} onChange={e => update(i, "email", e.target.value)} placeholder="Optional" /></div>
              </div>
            </div>
          );
        })}
        <Button variant="secondary" size="sm" onClick={() => onChange([...references, { ...EMPTY_REFERENCE }])}>
          <Plus size={12} /> Add another reference
        </Button>
      </CardContent>
    </Card>
  );
}
